import React, { useState, useEffect } from 'react';
import { Product, addProduct, updateProduct, uploadImage } from '../../firebase/productService';
import './AdminProductForm.css';

interface AdminProductFormProps {
  product?: Product | null;
  onClose: () => void;
  onSubmit: () => void;
}

const AdminProductForm: React.FC<AdminProductFormProps> = ({ product, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    type: '',
    release: '',
    hash: '',
    description: '',
    benifits: '',
    ritual: '',
    extras: '',
    colors: [] as string[]
  });

  const [images, setImages] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        price: product.price,
        type: product.type || '',
        release: product.release || '',
        hash: product.hash || '',
        description: product.description || '',
        benifits: product.benifits || '',
        ritual: product.ritual || '',
        extras: product.extras || '',
        colors: product.colors || []
      });
      setImages(product.images);
    }
  }, [product]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const handleImageReorder = (fromIndex: number, direction: 'left' | 'right') => {
    const newImages = [...images];
    const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;
    
    if (toIndex >= 0 && toIndex < newImages.length) {
      const [movedImage] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, movedImage);
      setImages(newImages);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveUploadedFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleReorderUploadedFile = (fromIndex: number, direction: 'left' | 'right') => {
    const newFiles = [...uploadedFiles];
    const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;
    
    if (toIndex >= 0 && toIndex < newFiles.length) {
      const [movedFile] = newFiles.splice(fromIndex, 1);
      newFiles.splice(toIndex, 0, movedFile);
      setUploadedFiles(newFiles);
    }
  };

  const uploadImages = async (): Promise<string[]> => {
    const uploadedUrls: string[] = [];
    
    for (const file of uploadedFiles) {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const path = `products/${fileName}`;
      
      try {
        const url = await uploadImage(file, path);
        uploadedUrls.push(url);
      } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
      }
    }
    
    return uploadedUrls;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (formData.price <= 0) newErrors.price = 'Price must be greater than 0';
    if (images.length === 0 && uploadedFiles.length === 0) newErrors.images = 'At least one image is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      let allImages = [...images];
      
      // If there are uploaded files, upload them first
      if (uploadedFiles.length > 0) {
        const newImageUrls = await uploadImages();
        allImages = [...allImages, ...newImageUrls];
      }
      
      const productData = {
        ...formData,
        img: allImages[0] || '',
        images: allImages,
        price: Number(formData.price)
      };

      if (product) {
        // Update existing product
        await updateProduct(product.id!, productData);
      } else {
        // Add new product
        await addProduct(productData);
      }
      
      onSubmit();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-form-overlay">
      <div className="admin-form-container">
        <div className="admin-form-header">
          <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Product Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="price">Price *</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={errors.price ? 'error' : ''}
              />
              {errors.price && <span className="error-message">{errors.price}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="type">Category</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="">Select Category</option>
                <option value="skincare">Skincare</option>
                <option value="hair">Hair</option>
                <option value="body">Body</option>
                <option value="skincare bundles">Skincare Bundles</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="release">Release Date</label>
              <input
                type="date"
                id="release"
                name="release"
                value={formData.release}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="hash">Hash</label>
              <input
                type="text"
                id="hash"
                name="hash"
                value={formData.hash}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="benifits">Benefits</label>
            <textarea
              id="benifits"
              name="benifits"
              value={formData.benifits}
              onChange={handleInputChange}
              rows={4}
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="ritual">Ritual</label>
            <textarea
              id="ritual"
              name="ritual"
              value={formData.ritual}
              onChange={handleInputChange}
              rows={6}
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="extras">Extras</label>
            <textarea
              id="extras"
              name="extras"
              value={formData.extras}
              onChange={handleInputChange}
              rows={4}
            />
          </div>

          <div className="form-group full-width">
            <label>Product Images *</label>
            <div className="image-upload-section">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="file-input"
              />
                             <div className="uploaded-files">
                 {uploadedFiles.map((file, index) => (
                   <div key={index} className="uploaded-file">
                     <img src={URL.createObjectURL(file)} alt="Preview" />
                     <div className="image-controls">
                       {index > 0 && (
                         <button
                           type="button"
                           onClick={() => handleReorderUploadedFile(index, 'left')}
                           className="reorder-btn left-arrow"
                           title="Move left"
                         >
                           ←
                         </button>
                       )}
                       {index < uploadedFiles.length - 1 && (
                         <button
                           type="button"
                           onClick={() => handleReorderUploadedFile(index, 'right')}
                           className="reorder-btn right-arrow"
                           title="Move right"
                         >
                           →
                         </button>
                       )}
                       <button
                         type="button"
                         onClick={() => handleRemoveUploadedFile(index)}
                         className="remove-file-btn"
                         title="Remove file"
                       >
                         ×
                       </button>
                     </div>
                   </div>
                 ))}
               </div>
            </div>
            {errors.images && <span className="error-message">{errors.images}</span>}
          </div>

                     {images.length > 0 && (
             <div className="form-group full-width">
               <label>Existing Images (use arrows to reorder)</label>
               <div className="existing-images">
                 {images.map((image, index) => (
                   <div key={index} className="existing-image">
                     <img src={image} alt={`Product ${index + 1}`} />
                     <div className="image-controls">
                       {index > 0 && (
                         <button
                           type="button"
                           onClick={() => handleImageReorder(index, 'left')}
                           className="reorder-btn left-arrow"
                           title="Move left"
                         >
                           ←
                         </button>
                       )}
                       {index < images.length - 1 && (
                         <button
                           type="button"
                           onClick={() => handleImageReorder(index, 'right')}
                           className="reorder-btn right-arrow"
                           title="Move right"
                         >
                           →
                         </button>
                       )}
                       <button
                         type="button"
                         onClick={() => handleRemoveImage(index)}
                         className="remove-image-btn"
                         title="Remove image"
                       >
                         ×
                       </button>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
           )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? 'Saving...' : (product ? 'Update Product' : 'Add Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductForm; 